import { API } from '../../../configs/axios/axios.config'

const DEFAULT_ERROR_MESSAGE = 'Unexpected server error occurred. Please try again later.'

const CACHE_NAME = 'rtkq-api-cache'

async function getCachedResponse(cacheKey) {
    const cache = await caches.open(CACHE_NAME)
    const cachedResponse = await cache.match(cacheKey)

    if (cachedResponse) return await cachedResponse.json()
    return null
}

async function cacheResponse(cacheKey, responseData) {
    const cache = await caches.open(CACHE_NAME)
    const responseToCache = new Response(JSON.stringify(responseData), {
        headers: { 'Content-Type': 'application/json' },
    })
    await cache.put(cacheKey, responseToCache)
}

function generateCacheKey(url, params) {
    const queryString = new URLSearchParams(params).toString()
    return `${url}?${queryString}`
}

export default async function baseQuery({
    url,
    method,
    params,
    body,
    enablePersistCache = false,
    responseType = 'json',
    ...rest
}) {
    try {
        const cacheKey = generateCacheKey(url, params)
        if (enablePersistCache) {
            const cachedData = await getCachedResponse(cacheKey)
            console.log('Cached' + cacheKey, cachedData)
            if (cachedData) return { data: cachedData }
        }

        const result = await API({
            url,
            method,
            params,
            data: body,
            responseType,
            ...rest,
        })

        if (enablePersistCache) await cacheResponse(cacheKey, result.data)

        return { data: result.data }
    } catch (error) {
        if (error.response) {
            // If the responseType was 'blob', attempt to parse the error as JSON
            if (responseType === 'blob' && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text()
                    const parsedError = JSON.parse(text) // Parse blob as JSON
                    return { error: parsedError?.detail || DEFAULT_ERROR_MESSAGE }
                } catch (parseError) {
                    console.error('Failed to parse error blob:', parseError)
                    return { error: DEFAULT_ERROR_MESSAGE }
                }
            }

            // Handle JSON errors directly
            return { error: error.response.data?.detail || DEFAULT_ERROR_MESSAGE }
        }

        console.error('Unexpected error:', error)
        return { error: DEFAULT_ERROR_MESSAGE }
    }
}

export async function invalidatePersistCache(url, params) {
    const cacheKey = generateCacheKey(url, params)
    const cache = await caches.open(CACHE_NAME)
    await cache.delete(cacheKey)
}
